/**
 * AGBPAGE.JS - Allgemeine Geschäftsbedingungen
 *
 * Weißes Design + Planivio Teal/Green (gleich wie DatenschutzPage)
 * Nutzt i18n-Texte aus I18nContext (legal.terms.*)
 *
 * @version 1.0.0
 */

import React, { useState } from 'react';
import { useTranslation, LANGUAGES } from '../context/I18nContext';
import { usePublicPageView } from '../hooks/useAnalytics';

const PLANIVIO = {
  teal: '#4A90A4',
  green: '#6BA893',
  gradient: 'linear-gradient(135deg, #4A90A4 0%, #6BA893 100%)',
  shadow: 'rgba(74, 144, 164, 0.3)'
};

const COLORS = {
  bgWhite: '#FFFFFF',
  bgLight: '#F8FAFC',
  textDark: '#1E293B',
  textMedium: '#475569',
  textMuted: '#94A3B8',
  border: '#E2E8F0',
  accent: PLANIVIO.teal
};

const sectionStyle = {
  marginBottom: '40px',
  paddingBottom: '30px',
  borderBottom: `1px solid ${COLORS.border}`
};

const headingStyle = {
  fontSize: '1.4rem',
  fontWeight: 700,
  marginBottom: '20px',
  color: COLORS.textDark
};

const textStyle = {
  color: COLORS.textMedium,
  fontSize: '1rem',
  lineHeight: 1.8,
  marginBottom: '15px'
};

const listStyle = {
  color: COLORS.textMedium,
  lineHeight: 2,
  paddingLeft: '20px',
  marginBottom: '15px'
};

const linkStyle = {
  color: COLORS.accent,
  textDecoration: 'none',
  cursor: 'pointer'
};

function AGBPage({ onNavigate }) {
  usePublicPageView('agb');
  const { t, language, setLanguage, currentLanguage } = useTranslation();
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);

  return (
    <div style={{ background: COLORS.bgWhite, minHeight: '100vh', color: COLORS.textDark }}>

      {/* Navigation */}
      <nav style={{
        padding: '20px 40px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        background: COLORS.bgWhite,
        backdropFilter: 'blur(10px)',
        zIndex: 1000,
        borderBottom: `1px solid ${COLORS.border}`,
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
      }}>
        <div
          onClick={() => onNavigate && onNavigate('landing')}
          style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}
        >
          <img
            src="/images/Logos/planivio_logo_primary_400px.png"
            alt="Planivio"
            style={{ height: '36px', width: 'auto' }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div style={{ display: 'none', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '1.5rem', fontWeight: 800, color: COLORS.accent }}>planivio</span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={() => setShowLanguageMenu(!showLanguageMenu)}
            style={{
              padding: '8px 14px',
              background: COLORS.bgLight,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '8px',
              color: COLORS.textMedium,
              cursor: 'pointer',
              fontSize: '0.95rem',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s'
            }}
          >
            <span>{currentLanguage.flag}</span>
            <span>{currentLanguage.code.toUpperCase()}</span>
            <span style={{ fontSize: '0.7rem', opacity: 0.5 }}>&#9660;</span>
          </button>

          <button
            onClick={() => onNavigate && onNavigate('landing')}
            style={{
              padding: '10px 24px',
              background: COLORS.bgLight,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '10px',
              color: COLORS.textMedium,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            &larr; {t('common.back')}
          </button>
        </div>
      </nav>

      {/* Content */}
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '60px 40px' }}>

        <h1 style={{
          fontSize: '2.5rem',
          fontWeight: 800,
          marginBottom: '20px',
          background: PLANIVIO.gradient,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          {t('legal.terms.title')}
        </h1>

        <p style={{ color: COLORS.textMuted, marginBottom: '15px', fontSize: '1rem' }}>
          {t('legal.terms.lastUpdated')}: Februar 2026
        </p>

        <p style={{
          color: COLORS.textMedium,
          marginBottom: '40px',
          fontSize: '0.95rem',
          fontStyle: 'italic',
          background: COLORS.bgLight,
          padding: '12px 16px',
          borderRadius: '8px',
          border: `1px solid ${COLORS.border}`
        }}>
          {t('legal.terms.legalNote')}
        </p>

        {/* Inhaltsverzeichnis */}
        <section style={{ ...sectionStyle, background: COLORS.bgLight, padding: '20px', borderRadius: '12px', border: `1px solid ${COLORS.border}` }}>
          <h2 style={headingStyle}>Inhaltsverzeichnis</h2>
          <ul style={{ color: COLORS.textMedium, lineHeight: 2, paddingLeft: '20px' }}>
            {[1,2,3,4,5,6,7,8,9,10,11,12].map(n => (
              <li key={n}>{t(`legal.terms.s${n}.title`)}</li>
            ))}
          </ul>
        </section>

        {/* § 1 Geltungsbereich */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>{t('legal.terms.s1.title')}</h2>
          <p style={textStyle}>{t('legal.terms.s1.p1')}</p>
          <p style={{ ...textStyle, background: COLORS.bgLight, padding: '15px', borderRadius: '8px', border: `1px solid ${COLORS.border}` }}>
            <strong>Brice Fotzo</strong><br />
            Johannisstr. 105<br />
            90419 Nürnberg, Deutschland<br />
            E-Mail: kontakt@planivio.de<br />
            ({t('legal.terms.provider')})
          </p>
          <p style={textStyle}>{t('legal.terms.s1.p2')}</p>
          <p style={textStyle}>{t('legal.terms.s1.p3')}</p>
          <p style={textStyle}>{t('legal.terms.s1.p4')}</p>
        </section>

        {/* § 2 Vertragsschluss */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>{t('legal.terms.s2.title')}</h2>
          <p style={textStyle}>{t('legal.terms.s2.p1')}</p>
          <p style={textStyle}>{t('legal.terms.s2.p2')}</p>
          <p style={textStyle}>{t('legal.terms.s2.p3')}</p>
          <p style={textStyle}>{t('legal.terms.s2.p4')}</p>
        </section>

        {/* § 3 Leistungsbeschreibung */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>{t('legal.terms.s3.title')}</h2>
          <p style={textStyle}>{t('legal.terms.s3.p1')}</p>
          <p style={textStyle}>{t('legal.terms.s3.p2')}</p>
          <ul style={listStyle}>
            <li>{t('legal.terms.s3.modules.shopping')}</li>
            <li>{t('legal.terms.s3.modules.recipes')}</li>
            <li>{t('legal.terms.s3.modules.calendar')}</li>
            <li>{t('legal.terms.s3.modules.budget')}</li>
            <li>{t('legal.terms.s3.modules.notes')}</li>
            <li>{t('legal.terms.s3.modules.tasks')}</li>
            <li>{t('legal.terms.s3.modules.ai')}</li>
          </ul>
          <p style={textStyle}>{t('legal.terms.s3.p3')}</p>
          <p style={textStyle}>{t('legal.terms.s3.p4')}</p>
        </section>

        {/* § 4 Beta-Phase */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>{t('legal.terms.s4.title')}</h2>
          <p style={textStyle}>{t('legal.terms.s4.p1')}</p>
          <p style={textStyle}>{t('legal.terms.s4.p2')}</p>
          <p style={textStyle}>{t('legal.terms.s4.p3')}</p>
          <p style={textStyle}>{t('legal.terms.s4.p4')}</p>
        </section>

        {/* § 5 Preise und Zahlung */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>{t('legal.terms.s5.title')}</h2>
          <p style={textStyle}>{t('legal.terms.s5.p1')}</p>
          <p style={textStyle}>{t('legal.terms.s5.p2')}</p>
          <p style={textStyle}>{t('legal.terms.s5.p3')}</p>
          <ul style={listStyle}>
            <li>{t('legal.terms.s5.payment.card')}</li>
            <li>{t('legal.terms.s5.payment.sepa')}</li>
            <li>{t('legal.terms.s5.payment.paypal')}</li>
          </ul>
          <p style={textStyle}>{t('legal.terms.s5.p4')}</p>
          <p style={textStyle}>{t('legal.terms.s5.p5')}</p>
        </section>

        {/* § 6 Vertragslaufzeit und Kündigung */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>{t('legal.terms.s6.title')}</h2>
          <p style={textStyle}>{t('legal.terms.s6.p1')}</p>
          <p style={textStyle}>{t('legal.terms.s6.p2')}</p>
          <p style={textStyle}>{t('legal.terms.s6.p3')}</p>
          <p style={textStyle}>{t('legal.terms.s6.p4')}</p>
        </section>

        {/* § 7 Nutzungsrechte und Pflichten */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>{t('legal.terms.s7.title')}</h2>
          <p style={textStyle}>{t('legal.terms.s7.p1')}</p>
          <p style={textStyle}>{t('legal.terms.s7.p2')}</p>
          <p style={textStyle}>{t('legal.terms.s7.p3')}</p>
          <ul style={listStyle}>
            <li>{t('legal.terms.s7.duties.illegal')}</li>
            <li>{t('legal.terms.s7.duties.commercial')}</li>
            <li>{t('legal.terms.s7.duties.impair')}</li>
            <li>{t('legal.terms.s7.duties.share')}</li>
            <li>{t('legal.terms.s7.duties.reverse')}</li>
          </ul>
          <p style={textStyle}>{t('legal.terms.s7.p4')}</p>
        </section>

        {/* § 8 Datenschutz */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>{t('legal.terms.s8.title')}</h2>
          <p style={textStyle}>{t('legal.terms.s8.p1')}</p>
          <p style={textStyle}>
            {t('legal.terms.s8.p2a')}{' '}
            <span onClick={() => onNavigate && onNavigate('datenschutz')} style={linkStyle}>
              {t('legal.terms.s8.privacyLink')}
            </span>
            {t('legal.terms.s8.p2b')}
          </p>
          <p style={textStyle}>{t('legal.terms.s8.p3')}</p>
        </section>

        {/* § 9 Haftung */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>{t('legal.terms.s9.title')}</h2>
          <p style={textStyle}>{t('legal.terms.s9.p1')}</p>
          <p style={textStyle}>{t('legal.terms.s9.p2')}</p>
          <p style={textStyle}>{t('legal.terms.s9.p3')}</p>
          <p style={textStyle}>{t('legal.terms.s9.p4')}</p>
          <p style={textStyle}>{t('legal.terms.s9.p5')}</p>
        </section>

        {/* § 10 Gewährleistung */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>{t('legal.terms.s10.title')}</h2>
          <p style={textStyle}>{t('legal.terms.s10.p1')}</p>
          <p style={textStyle}>{t('legal.terms.s10.p2')}</p>
          <p style={textStyle}>{t('legal.terms.s10.p3')}</p>
          <p style={textStyle}>{t('legal.terms.s10.p4')}</p>
        </section>

        {/* § 11 Änderungen der AGB */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>{t('legal.terms.s11.title')}</h2>
          <p style={textStyle}>{t('legal.terms.s11.p1')}</p>
          <p style={textStyle}>{t('legal.terms.s11.p2')}</p>
          <p style={textStyle}>{t('legal.terms.s11.p3')}</p>
          <p style={textStyle}>{t('legal.terms.s11.p4')}</p>
        </section>

        {/* § 12 Schlussbestimmungen */}
        <section style={{ ...sectionStyle, borderBottom: 'none' }}>
          <h2 style={headingStyle}>{t('legal.terms.s12.title')}</h2>
          <p style={textStyle}>{t('legal.terms.s12.p1')}</p>
          <p style={textStyle}>{t('legal.terms.s12.p2')}</p>
          <p style={textStyle}>{t('legal.terms.s12.p3')}</p>
          <p style={textStyle}>
            {t('legal.terms.s12.p4').replace('https://ec.europa.eu/consumers/odr', '')}{' '}
            <a
              href="https://ec.europa.eu/consumers/odr"
              target="_blank"
              rel="noopener noreferrer"
              style={linkStyle}
            >
              https://ec.europa.eu/consumers/odr
            </a>
          </p>
          <p style={{ color: COLORS.textMuted, fontSize: '0.9rem', marginTop: '30px' }}>
            {t('legal.terms.lastUpdated')}: Februar 2026
          </p>
        </section>

      </div>

      {/* Footer */}
      <footer style={{
        padding: '30px 40px',
        borderTop: `1px solid ${COLORS.border}`,
        background: COLORS.bgLight,
        textAlign: 'center'
      }}>
        <div style={{ display: 'flex', gap: '25px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <span
            onClick={() => onNavigate && onNavigate('datenschutz')}
            style={{ color: COLORS.textMedium, fontSize: '0.9rem', cursor: 'pointer' }}
          >
            {t('landing.footer.privacy')}
          </span>
          <span style={{ color: COLORS.accent, fontSize: '0.9rem', fontWeight: 600 }}>
            {t('landing.footer.terms')}
          </span>
          <span
            onClick={() => onNavigate && onNavigate('impressum')}
            style={{ color: COLORS.textMedium, fontSize: '0.9rem', cursor: 'pointer' }}
          >
            {t('landing.footer.imprint')}
          </span>
        </div>
        <div style={{ marginTop: '20px', color: COLORS.textMuted, fontSize: '0.8rem' }}>
          &copy; 2026 Planivio. {t('landing.footer.rights')}
        </div>
      </footer>

      {/* Language Dropdown */}
      {showLanguageMenu && (
        <>
          <div
            onClick={() => setShowLanguageMenu(false)}
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99998 }}
          />
          <div style={{
            position: 'fixed',
            top: '70px',
            right: '20px',
            background: COLORS.bgWhite,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '12px',
            padding: '8px',
            minWidth: '160px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
            zIndex: 99999
          }}>
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => { setLanguage(lang.code); setShowLanguageMenu(false); }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: language === lang.code ? PLANIVIO.gradient : 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  color: language === lang.code ? 'white' : COLORS.textDark,
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  transition: 'all 0.2s',
                  textAlign: 'left',
                  marginBottom: '4px'
                }}
              >
                <span style={{ fontSize: '1.3rem' }}>{lang.flag}</span>
                <span>{lang.name}</span>
                {language === lang.code && <span style={{ marginLeft: 'auto', opacity: 0.8 }}>&#10003;</span>}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default AGBPage;
